import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Sparkles, Loader2, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const withBackend = (u) => (u && u.startsWith("/api/media/") ? `${BACKEND}${u}` : u);

const EMPTY_EVENT = { year: "", title: "", description: "", icon_url: "" };

function SortableRow({ id, ev, idx, upd, remove, generateIcon, aiBusy }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="border border-black/10 rounded-lg p-4 bg-white grid md:grid-cols-12 gap-3" data-testid={`t38-event-${idx}`}>
      <div className="md:col-span-1 flex md:flex-col items-center justify-between gap-2">
        <button {...attributes} {...listeners} className="cursor-grab text-neutral-400 hover:text-black touch-none" title="Reordenar" aria-label="Reordenar"><GripVertical className="w-5 h-5" /></button>
        <button onClick={() => remove(idx)} className="text-red-500 hover:text-red-700" title="Remover" aria-label="Remover"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="md:col-span-2">
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Ano</label>
        <input value={ev.year} onChange={(e) => upd(idx, "year", e.target.value)} placeholder="1988" data-testid={`t38-year-${idx}`} className="w-full border border-black/15 rounded px-3 py-2 text-sm" />
        <label className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2 block">Título (opcional)</label>
        <input value={ev.title || ""} onChange={(e) => upd(idx, "title", e.target.value)} placeholder="Fundação" className="w-full border border-black/15 rounded px-3 py-2 text-sm" />
      </div>
      <div className="md:col-span-6">
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Descrição do evento</label>
        <textarea value={ev.description} onChange={(e) => upd(idx, "description", e.target.value)} rows={3} placeholder="Ex: A Places nasce em Goiânia com propósito de transformar o marketing imobiliário." data-testid={`t38-desc-${idx}`} className="w-full border border-black/15 rounded px-3 py-2 text-sm" />
      </div>
      <div className="md:col-span-3">
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Ícone (IA)</label>
        <div className="border border-dashed border-black/15 rounded-lg h-24 flex items-center justify-center overflow-hidden bg-black/[0.02]">
          {ev.icon_url ? (
            <img src={withBackend(ev.icon_url)} alt="" className="w-20 h-20 object-contain" />
          ) : (
            <div className="text-[10px] text-neutral-400">Sem ícone</div>
          )}
        </div>
        <button
          onClick={() => generateIcon(idx)}
          disabled={aiBusy === idx || !ev.description}
          data-testid={`t38-ai-${idx}`}
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold bg-accent-orange text-white px-3 py-2 rounded-full hover:bg-black disabled:opacity-50 transition-colors"
        >
          {aiBusy === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Gerar ícone
        </button>
        <input value={ev.icon_url || ""} onChange={(e) => upd(idx, "icon_url", e.target.value)} placeholder="/api/media/... ou URL" className="mt-1 w-full border border-black/15 rounded px-2 py-1 text-[10px] font-mono" />
        <div className="mt-2">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Cor de fundo do bloco</label>
          <div className="flex gap-1 items-center">
            <input type="color" value={ev.background_color || "#ffffff"} onChange={(e) => upd(idx, "background_color", e.target.value)} className="w-8 h-8 border border-black/15 rounded cursor-pointer" data-testid={`t38-color-${idx}`} />
            <input type="text" value={ev.background_color || ""} onChange={(e) => upd(idx, "background_color", e.target.value)} placeholder="vazio" className="flex-1 border border-black/15 rounded px-2 py-1 text-[10px] font-mono" />
            {ev.background_color && <button onClick={() => upd(idx, "background_color", "")} className="text-[9px] text-red-500">×</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Timeline38Editor() {
  const [enabled, setEnabled] = useState(true);
  const [eyebrow, setEyebrow] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    api.get("/content/kv/timeline_38").then(({ data }) => {
      if (data && typeof data === "object") {
        setEnabled(data.enabled !== false);
        setEyebrow(data.eyebrow || "");
        setTitle(data.title || "");
        setSubtitle(data.subtitle || "");
        setEvents(Array.isArray(data.events) ? data.events.map((e, i) => ({ ...e, _id: e._id || `ev_${i}_${Date.now()}` })) : []);
      }
    }).catch(() => {});
  }, []);

  const add = () => setEvents((arr) => [...arr, { ...EMPTY_EVENT, _id: `ev_${Date.now()}_${Math.random()}` }]);
  const remove = (i) => setEvents((arr) => arr.filter((_, j) => j !== i));
  const upd = (i, k, v) => setEvents((arr) => { const a = [...arr]; a[i] = { ...a[i], [k]: v }; return a; });

  const generateIcon = async (i) => {
    const ev = events[i];
    if (!ev.description) { toast.error("Preencha a descrição primeiro."); return; }
    setAiBusy(i);
    try {
      const prompt = ev.title ? `${ev.title}: ${ev.description}` : ev.description;
      const { data } = await api.post("/ai/generate-icon", { prompt });
      if (data?.url) {
        upd(i, "icon_url", data.url);
        toast.success("Ícone gerado.");
      }
    } catch (e) {
      toast.error("Falha ao gerar ícone: " + (e?.response?.data?.detail || e.message || ""));
    } finally { setAiBusy(null); }
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setEvents((arr) => {
      const oldIndex = arr.findIndex((e) => e._id === active.id);
      const newIndex = arr.findIndex((e) => e._id === over.id);
      return arrayMove(arr, oldIndex, newIndex);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = events.map(({ _id, ...rest }) => rest);
      await api.put("/content/kv/timeline_38", { value: { enabled, eyebrow, title, subtitle, events: clean } });
      toast.success("Linha do tempo salva.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl">Linha do Tempo &mdash; 38 Anos</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">Timeline vertical na página dedicada <b>/38-anos</b> (acessível pelo link serifado itálico "38 anos" no cabeçalho). Cada evento pode ter um ícone pictográfico gerado por IA a partir da descrição.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="t38-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange transition-colors disabled:opacity-50">
          <Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}
        </button>
      </div>

      <section className="border border-black/10 rounded-md p-5 mb-6 grid md:grid-cols-2 gap-4">
        <label className="flex items-center gap-2 text-sm md:col-span-2">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="t38-enabled" /> Publicar timeline na página /38-anos
        </label>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Eyebrow</label>
          <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} placeholder="Linha do tempo" className="w-full border border-black/15 rounded px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Título</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="38 anos construindo o futuro do marketing imobiliário." className="w-full border border-black/15 rounded px-3 py-2 text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Subtítulo</label>
          <textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2} className="w-full border border-black/15 rounded px-3 py-2 text-sm" />
        </div>
      </section>

      <div className="flex justify-between items-center mb-3">
        <div className="text-xs uppercase tracking-widest text-accent-orange font-bold">Eventos ({events.length})</div>
        <button onClick={add} data-testid="t38-add" className="text-xs bg-black text-white px-3 py-2 rounded-full font-semibold hover:bg-accent-yellow hover:text-black transition-colors">
          <Plus className="w-3 h-3 inline mr-1" /> Adicionar evento
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={events.map((e) => e._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {events.map((ev, i) => (
              <SortableRow key={ev._id} id={ev._id} ev={ev} idx={i} upd={upd} remove={remove} generateIcon={generateIcon} aiBusy={aiBusy} />
            ))}
            {events.length === 0 && (
              <div className="text-center border border-dashed border-black/15 rounded p-8 text-sm text-neutral-500">
                Nenhum evento cadastrado. Clique em <b>Adicionar evento</b> para começar.
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
